import { isRialString, normalizeIranMobile, toLatinDigits } from '@clinic/money-date';
import { z } from 'zod';

export const ORG_ID_HEADER = 'x-org-id';
export const REQUEST_ID_HEADER = 'x-request-id';
export const IDEMPOTENCY_KEY_HEADER = 'idempotency-key';

export const UuidSchema = z.uuid();
export const IsoDateTimeSchema = z.iso.datetime({ offset: true });

/** Money on the wire: integer rial as a decimal string (ADR-014). Never a JSON number. */
export const RialStringSchema = z
  .string()
  .refine(isRialString, { message: 'مبلغ باید عدد صحیح ریالی به‌صورت متن باشد.' });

/**
 * The most one price, cost or entry may hold: 10^13 rial, a thousand billion toman. Far above any
 * clinic's real figure and far below the database's bigint ceiling, so a typo with extra zeros is
 * refused with a message instead of reaching the books, and a sum of entries can never overflow.
 */
export const AMOUNT_MAX_RIAL = 10_000_000_000_000n;
export const AMOUNT_TOO_BIG = 'مبلغ بیش از حد مجاز است.';
export const AMOUNT_NEGATIVE = 'مبلغ نمی‌تواند منفی باشد.';

/** A price or cost typed by a person: an integer rial string from zero up to the ceiling. */
export const AmountRialSchema = z.string().superRefine((v, ctx) => {
  if (!isRialString(v)) {
    ctx.addIssue({ code: 'custom', message: 'مبلغ باید عدد صحیح ریالی به‌صورت متن باشد.' });
    return;
  }
  const n = BigInt(v);
  if (n < 0n) ctx.addIssue({ code: 'custom', message: AMOUNT_NEGATIVE });
  else if (n > AMOUNT_MAX_RIAL) ctx.addIssue({ code: 'custom', message: AMOUNT_TOO_BIG });
});

/** Accepts any common way of typing an Iranian mobile number and outputs E.164 (BR-22). */
export const PhoneInputSchema = z
  .string()
  .trim()
  .min(1, { message: 'شماره‌ی موبایل را وارد کنید.' })
  .max(32)
  .transform((value, ctx) => {
    const normalized = normalizeIranMobile(value);
    if (!normalized) {
      ctx.addIssue({ code: 'custom', message: 'شماره‌ی موبایل معتبر نیست.' });
      return z.NEVER;
    }
    return normalized;
  });

export const DisplayNameSchema = z
  .string()
  .trim()
  .min(1, { message: 'نام را وارد کنید.' })
  .max(100, { message: 'نام حداکثر ۱۰۰ نویسه است.' });

export const PAGE_LIMIT_MAX = 100;
export const PAGE_LIMIT_DEFAULT = 20;

/** Cursor pagination (Master Prompt §56). The cursor is the last item's id (UUIDv7 is time-ordered). */
export const CursorPageQuerySchema = z.object({
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(PAGE_LIMIT_DEFAULT),
});
export type CursorPageQuery = z.infer<typeof CursorPageQuerySchema>;

export function pageSchema<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.uuid().nullable(),
  });
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export const IdParamSchema = z.uuid();

/**
 * Ids in a query string, comma separated (`?patientIds=a,b`): 1 to `max`, no repeats. For one
 * page of a list asking another module about its rows.
 */
export function idListQuery(max: number) {
  return z
    .string()
    .max(max * 37)
    .transform((v) => v.split(',').map((s) => s.trim()))
    .pipe(
      z
        .array(z.uuid({ message: 'شناسه معتبر نیست.' }))
        .min(1)
        .max(max, { message: `حداکثر ${max} شناسه.` })
        .refine((v) => new Set(v).size === v.length, { message: 'شناسه‌ی تکراری است.' }),
    );
}

/** One page of patients (at most `PAGE_LIMIT_MAX`) asked about by another module. */
export const PatientIdsQuerySchema = z.object({ patientIds: idListQuery(PAGE_LIMIT_MAX) });
export type PatientIdsQuery = z.infer<typeof PatientIdsQuerySchema>;

/**
 * A calendar date without time (`YYYY-MM-DD`, Gregorian), e.g. birth dates. Years 1900 to 2200
 * only: a date outside them is a typo, and the database's `date` cannot hold year 0 at all.
 */
export const DateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'تاریخ معتبر نیست.' })
  .refine(
    (v) => {
      const d = new Date(`${v}T00:00:00Z`);
      return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
    },
    { message: 'تاریخ معتبر نیست.' },
  )
  .refine((v) => v >= '1900-01-01' && v <= '2200-12-31', { message: 'تاریخ معتبر نیست.' });

/** A moment a person books or moves an appointment to: years 2000 to 2100. */
export const BookingInstantSchema = IsoDateTimeSchema.refine(
  (v) => {
    const t = Date.parse(v);
    return t >= Date.UTC(2000, 0, 1) && t < Date.UTC(2101, 0, 1);
  },
  { message: 'زمان نوبت معتبر نیست.' },
);

/** Optional free text: trimmed, empty becomes null. */
export function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max, { message: `حداکثر ${max} نویسه.` })
    .nullish()
    .transform((v) => (v ? v : null));
}

/** OTP code typed by a user; Persian digits are accepted. */
export const OtpCodeSchema = z
  .string()
  .trim()
  .transform((value) => toLatinDigits(value))
  .pipe(z.string().regex(/^\d{6}$/, { message: 'کد باید ۶ رقم باشد.' }));
